import { RequireAtLeastOne } from '@skylab-shared/types';
import { Calc, MidpointRounding } from '@skylab-shared/utils';
import { DocumentItem, DocumentItemCalculationContext, DocumentItemCalculator, DocumentItemChanges, DocumentItemChangesProperties } from '@skylab/calculations/document-item';

export interface MarginItem extends DocumentItem {
    unitCost?: number;
    cost?: number;
    exchangeRate?: number;
}

export type MinimalMarginItem = RequireAtLeastOne<MarginItem, 'netPrice' | 'grossPrice' | 'netValue' | 'grossValue' | 'vatValue'>;

export class DocumentItemMarginCalculator extends DocumentItemCalculator<MinimalMarginItem> {
    public static costChanged = (propertyName: string) => /cost/i.test(propertyName);
    public static unitCostChanged = (propertyName: string) => /unitCost/i.test(propertyName);

    public override recalculate(item: MinimalMarginItem, { precision }: DocumentItemCalculationContext, triggeredBy?: DocumentItemChangesProperties | string): Readonly<Required<MarginItem>> {
        const result = { ...item };

        const tBy = triggeredBy as DocumentItemChangesProperties;

        if (!tBy) {
            this.recalculateFromUnitCost(result, precision);
        } else if (DocumentItemChanges.vatRateChanged(tBy)) {
            this.recalculateFromUnitCost(result, precision);
        } else if (DocumentItemChanges.quantityChanged(tBy)) {
            this.recalculateFromUnitCost(result, precision);
        } else if (DocumentItemChanges.discountKindChanged(tBy) || DocumentItemChanges.discountChanged(tBy)) {
            this.recalculateFromUnitCost(result, precision);
        } else if (DocumentItemChanges.priceChanged(tBy) && DocumentItemChanges.grossChanged(tBy)) {
            this.recalculateFromUnitCost(result, precision);
        } else if (DocumentItemChanges.valueChanged(tBy) && DocumentItemChanges.grossChanged(tBy)) {
            this.recalculateFromGrossValue(result, precision);
        } else if (DocumentItemMarginCalculator.unitCostChanged(tBy)) {
            this.recalculateFromUnitCost(result, precision);
        } else if (DocumentItemMarginCalculator.costChanged(tBy)) {
            this.recalculateFromUnitCost(result, precision); // było recalculateFromCost, ale teraz cost jest wyznaczany wcześniej, więc można od razu od costu liczyć
        } else
            throw new RangeError(`Property name out of range: ${tBy}`);

        return <Required<MarginItem>> result;
    }

    private recalculateFromUnitCost(item: MarginItem, precision: number): void {
        item.cost = Calc.round(item.unitCost! * item.quantity, precision, MidpointRounding.AwayFromZero);
        item.grossValue = Calc.round(item.grossPrice! * item.quantity, precision, MidpointRounding.AwayFromZero);

        const costInCurrency = Calc.round(item.cost / (item.exchangeRate || 1), precision, MidpointRounding.AwayFromZero);
        const baseForVat = Calc.round(item.grossValue - costInCurrency, precision, MidpointRounding.AwayFromZero);

        item.vatValue = Calc.round(baseForVat * (item.vatRate / (1 + item.vatRate)), precision, MidpointRounding.AwayFromZero);
        item.netValue = Calc.round(item.grossValue - item.vatValue, precision, MidpointRounding.AwayFromZero);
        item.netPrice = Calc.round(item.netValue / item.quantity, precision, MidpointRounding.AwayFromZero);
    }

    private recalculateFromGrossValue(item: MarginItem, precision: number): void {
        item.grossPrice = Calc.round(item.grossValue! / item.quantity, precision, MidpointRounding.AwayFromZero);

        this.recalculateFromUnitCost(item, precision);
    }
}
